import { useQuery } from "@tanstack/react-query";
import { SOL_ADDRESS } from "@/lib/utils";

export interface TokenBalanceValue {
	balance: number;
	usdValue: number;
	pricePerToken: number;
}

export type TokenBalanceMap = Record<string, TokenBalanceValue>;

interface HeliusBalanceEntry {
	mint: string;
	symbol?: string;
	name?: string;
	balance: number | string;
	decimals: number;
	usdValue?: number | string;
	pricePerToken?: number | string;
	logoUri?: string;
	tokenProgram?: string;
}

interface HeliusBalanceResponse {
	balances: HeliusBalanceEntry[];
	totalUsdValue?: number;
	pagination?: {
		page: number;
		limit: number;
		hasMore: boolean;
	};
}

const HELIUS_WALLET_API_URL = "https://api.helius.xyz/v1/wallet";
const HELIUS_NATIVE_SOL_MINT = "So11111111111111111111111111111111111111111";
const BALANCE_PAGE_LIMIT = 100;
const POLL_INTERVAL_MS = 60_000;

function getHeliusApiKey(): string {
	const explicitKey = import.meta.env.VITE_HELIUS_API_KEY?.trim();
	if (explicitKey) return explicitKey;

	try {
		const rpcUrl = new URL(import.meta.env.VITE_SOLANA_RPC_URL);
		return rpcUrl.searchParams.get("api-key")?.trim() ?? "";
	} catch {
		return "";
	}
}

function getBalanceUrl(walletAddress: string, page: number): string {
	const apiKey = getHeliusApiKey();
	if (!apiKey) {
		throw new Error(
			"Missing Helius API key. Set VITE_HELIUS_API_KEY or include api-key in VITE_SOLANA_RPC_URL.",
		);
	}

	const url = new URL(
		`${HELIUS_WALLET_API_URL}/${encodeURIComponent(walletAddress)}/balances`,
	);
	url.searchParams.set("api-key", apiKey);
	url.searchParams.set("page", String(page));
	url.searchParams.set("limit", String(BALANCE_PAGE_LIMIT));
	return url.toString();
}

function normalizeMint(entry: HeliusBalanceEntry): string {
	if (entry.mint === HELIUS_NATIVE_SOL_MINT) {
		return SOL_ADDRESS;
	}

	return entry.mint;
}

function addBalance(map: TokenBalanceMap, entry: HeliusBalanceEntry) {
	const balance = Number(entry.balance);
	if (!Number.isFinite(balance)) return;

	const usdValue = Number(entry.usdValue) || 0;
	const pricePerToken =
		Number(entry.pricePerToken) || (balance > 0 ? usdValue / balance : 0);
	const mint = normalizeMint(entry);
	const existing = map[mint];

	map[mint] = {
		balance: (existing?.balance ?? 0) + balance,
		usdValue: (existing?.usdValue ?? 0) + usdValue,
		pricePerToken: pricePerToken || existing?.pricePerToken || 0,
	};
}

async function fetchBalancePage(
	walletAddress: string,
	page: number,
): Promise<HeliusBalanceResponse> {
	const res = await fetch(getBalanceUrl(walletAddress, page));
	const body = await res.text();

	if (!res.ok) {
		throw new Error(`Helius balance fetch failed: ${res.status}${body ? ` ${body}` : ""}`);
	}

	return JSON.parse(body) as HeliusBalanceResponse;
}

async function fetchBalances(walletAddress: string): Promise<TokenBalanceMap> {
	const map: TokenBalanceMap = {};
	let page = 1;
	let hasMore = true;

	while (hasMore) {
		const data = await fetchBalancePage(walletAddress, page);

		for (const entry of data.balances ?? []) {
			addBalance(map, entry);
		}

		hasMore = data.pagination?.hasMore ?? false;
		page += 1;
	}

	return map;
}

export function useBalance(walletAddress: string | undefined) {
	return useQuery({
		queryKey: ["balance", walletAddress],
		queryFn: () => fetchBalances(walletAddress!),
		enabled: Boolean(walletAddress),
		refetchInterval: POLL_INTERVAL_MS,
		refetchIntervalInBackground: false,
		staleTime: 30_000,
		retry: 2,
	});
}
